import { inject, Pipe, PipeTransform } from '@angular/core';
import { AddressComponent, LatLonModel } from '@data-access';
import { GeocodingService } from '../services/geocoding.service';
import { AddressComponentType, getAddressPart } from '../utils/get-address-part';

const COORDINATE_DISPLAY_PRECISION = 6;

/**
 * Formats a geographic point as a human-readable address string.
 * Accepts AddressComponentType keys as variadic arguments to select which parts
 * to include (e.g. 'locality', 'administrative_area_level_1', 'postal_code').
 *
 * Case 1 — addressComponents provided: formats directly, no network call.
 * Case 2 — only coordinates: reverse-geocodes via Google Maps and formats the result.
 * Case 3 — geocoding fails or returns no match: falls back to "lat, lng" decimal string.
 */
@Pipe({
  name: 'fr8botAddress',
  standalone: true,
  pure: true,
})
export class Fr8botAddressPipe implements PipeTransform {
  private readonly geocodingService = inject(GeocodingService);

  async transform(
    addressComponents: AddressComponent[] | undefined,
    fallbackPoint: LatLonModel | undefined,
    ...types: AddressComponentType[]
  ) {
    if (addressComponents?.length) {
      const formatted = getAddressPart(addressComponents, ...types);
      if (formatted) {
        return formatted;
      }
    }

    if (fallbackPoint?.latitude && fallbackPoint?.longitude) {
      const geocodedComponents = await this.geocodingService.reverseGeocodeComponents({
        lat: fallbackPoint.latitude,
        lng: fallbackPoint.longitude,
      });

      const geocodedStr = getAddressPart(geocodedComponents, ...types);
      if (geocodedStr) {
        return geocodedStr;
      }

      return `${fallbackPoint.latitude.toFixed(COORDINATE_DISPLAY_PRECISION)}, ${fallbackPoint.longitude.toFixed(COORDINATE_DISPLAY_PRECISION)}`;
    }

    return '';
  }
}
